import {
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  NgbModal,
  NgbModalModule,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import { first, Subscription } from 'rxjs';
import Fuse from 'fuse.js';

import { Airport } from '../../models/app';
import { ClickStopPropagationDirective } from '../../directives';
import { AppStore } from '../../store';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-airport-picker',
  templateUrl: './airport-picker.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgbModalModule,
    ClickStopPropagationDirective,
    ScrollingModule,
  ],
})
export class AirportPickerComponent implements OnInit, OnDestroy {
  private subscriptions$: Subscription[] = [];
  private content = viewChild<TemplateRef<any>>('content');
  private body = viewChild<ElementRef>('body');
  private scrollViewport = viewChild(CdkVirtualScrollViewport);
  private searchControl = viewChild<ElementRef>('searchControl');
  private modalService = inject(NgbModal);
  private fb = inject(FormBuilder);
  private modalRef?: NgbModalRef;
  readonly headerTitle = input('Chọn địa điểm');
  readonly selected = output<Airport>();
  readonly appStore = inject(AppStore);
  readonly cdnUrl = environment.cdnUrl;

  airports = this.appStore.common.airports();
  dataForm!: FormGroup;

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach((s) => s?.unsubscribe());
  }

  open() {
    this.modalRef = this.modalService.open(this.content(), {
      centered: false,
      size: 'md',
    });

    this.modalRef.shown.pipe(first()).subscribe(() => {
      this.searchControl()?.nativeElement.focus();
    });

    this.modalRef.result.then(
      (result) => {
        this.dataForm.reset();
      },
      (reason) => {
        this.dataForm.reset();
      },
    );
  }

  onSelect(value: Airport) {
    this.selected.emit(value);
    this.modalRef?.close();
  }

  private initForm() {
    this.dataForm = this.fb.group({
      search: [],
    });

    this.subscriptions$.push(
      this.dataForm.controls['search'].valueChanges.subscribe((value) => {
        this.body()?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
        this.scrollViewport()?.scrollTo({ top: 0, behavior: 'smooth' });
        if (value) {
          const fuse = new Fuse(this.appStore.common.airports(), {
            includeScore: true,
            keys: ['keywords', 'name', 'city', 'code', 'country'],
          });
          this.airports = fuse
            .search(value)
            .sort((a, b) => {
              const aIsVietnam = a.item.country === 'Việt Nam';
              const bIsVietnam = b.item.country === 'Việt Nam';

              const scoreA = a.score ?? 1;
              const scoreB = b.score ?? 1;

              // Only boost Vietnam if the score is decent (< 0.15 means reasonable match)
              const scoreThreshold = 0.15;

              if (
                aIsVietnam &&
                scoreA < scoreThreshold &&
                (!bIsVietnam || scoreB >= scoreThreshold)
              ) {
                return -1;
              }
              if (
                bIsVietnam &&
                scoreB < scoreThreshold &&
                (!aIsVietnam || scoreA >= scoreThreshold)
              ) {
                return 1;
              }

              // Otherwise, sort by original score
              return scoreA - scoreB;
            })
            .map((resultItem) => resultItem.item);
        } else {
          this.airports = this.appStore.common.airports();
        }
      }),
    );
  }
}
